/**
 * How the portal sends someone to the signing page, and what it may carry.
 *
 * The challenge id is the only thing in the URL. The code a person compares
 * always comes from the server, never from a link, so a link cannot show a
 * code that matches a screen the attacker controls.
 *
 * In-portal callers pass extras through router state, which a link from
 * outside cannot set:
 * - `returnTo`: where to go once signed (My Access, or the approval page);
 * - `actionAssigneeId`: the approval assignment the person came from, which
 *   the server uses to retire that assignment's chat buttons;
 * - `comments`: the note an approver typed before choosing to approve, which
 *   the server records with the decision;
 * - `fromPortal`: the portal minted this challenge and sent the person here.
 *   The code is for matching against the CLI, the extension or the desktop
 *   app; a challenge the portal started has no other screen to match, so the
 *   page leaves the code out. Only a link from outside, with no state, shows it;
 * - `enrolHere`: open at adding a passkey on this device, then sign with it.
 *   The Request access modal's "Add one here" sets it, for a member whose
 *   passkeys are all on other devices.
 */
import { safeRedirectPath } from "modules/members/safeRedirect";
import type { StepUpPurpose } from "state/members/stepUp";

export type StepUpLocationState = {
  returnTo?: string;
  actionAssigneeId?: string;
  comments?: string;
  fromPortal?: true;
  enrolHere?: true;
};

export const stepUpPath = (challengeId: string) =>
  `/members/step-up/${encodeURIComponent(challengeId)}`;

export const MY_ACCESS_PATH = "/members/my-access";
export const TASKS_PATH = "/members/assigned-tasks";

export const approvalRequestPath = (assigneeId: string) =>
  `/members/approval-requests/${encodeURIComponent(assigneeId)}`;

/** What a requester brings when the portal sends them to sign their draft. */
export const portalSigningState = (returnTo: string): StepUpLocationState => ({
  returnTo,
  fromPortal: true,
});

/**
 * What an approver brings to the signing page: the assignment they are
 * answering, so the server retires its chat buttons as an inline approval
 * would, and where to go once signed.
 */
export const approvalSigningState = (
  assigneeId: string,
  returnTo: string,
  comments?: string,
): StepUpLocationState => ({
  ...portalSigningState(returnTo),
  actionAssigneeId: assigneeId,
  ...(comments ? { comments } : {}),
});

/**
 * Where the Request access modal sends a draft it cannot sign in place: the
 * member has no passkey yet, or none on this device (`enrolHere`). The page
 * can add one. Back to where the person was once signed, or to My Access when
 * other drafts wait there.
 */
export const draftHandoff = (
  challengeId: string,
  here: string,
  othersWaiting: number,
  { enrolHere = false }: { enrolHere?: boolean } = {},
): { to: string; state: StepUpLocationState } => ({
  to: stepUpPath(challengeId),
  state: {
    ...portalSigningState(
      othersWaiting > 0 ? MY_ACCESS_PATH : (safeRedirectPath(here) ?? MY_ACCESS_PATH),
    ),
    ...(enrolHere ? { enrolHere: true as const } : {}),
  },
});

// Ids are ULIDs; anything else is not an id the server issued.
const ID_RE = /^[0-9A-Za-z_-]{1,64}$/;

/** Reads router state defensively: history state outlives the code that set it. */
export const readStepUpState = (
  state: unknown,
): {
  returnTo: string | null;
  actionAssigneeId: string | null;
  comments: string | null;
  fromPortal: boolean;
  enrolHere: boolean;
} => {
  const record =
    state && typeof state === "object" ? (state as Record<string, unknown>) : {};
  const returnTo =
    typeof record.returnTo === "string" ? safeRedirectPath(record.returnTo) : null;
  const actionAssigneeId =
    typeof record.actionAssigneeId === "string" &&
    ID_RE.test(record.actionAssigneeId)
      ? record.actionAssigneeId
      : null;
  const comments =
    typeof record.comments === "string" && record.comments.trim()
      ? record.comments.trim()
      : null;
  // Exactly true: anything else is not state the portal wrote.
  const fromPortal = record.fromPortal === true;
  const enrolHere = record.enrolHere === true;
  return { returnTo, actionAssigneeId, comments, fromPortal, enrolHere };
};

/** Where "done" leads when the caller did not say: the place the request lives. */
export const defaultReturn = (purpose: StepUpPurpose | undefined): string =>
  purpose === "approval" ? TASKS_PATH : MY_ACCESS_PATH;

export const returnLabel = (
  purpose: StepUpPurpose | undefined,
  returnTo: string | null,
): string => {
  if (purpose === "approval") {
    return returnTo ? "Back to the request" : "Go to your tasks";
  }
  return "Go to My Access";
};
